import { useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl';
import { get, isEmpty, isNumber } from 'lodash';
import { Dropdown, Input, InputNumber, Menu, RefSelectProps, Select, Tooltip } from 'antd';

import { EQUAL_FILTER, GREATER_FILTER, ICondoWeeksFilters, LOWER_FILTER, RANGE_FILTER } from '@common-types';
import { BlueButton } from '@share/components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

import './style.scss';
import { WhiteButton } from '../../common';
import moment from 'moment';

const CHECKIN_TYPE = 'checkin';
const CHECKOUT_TYPE = 'checkout';
const LOCATION_TYPE = 'locationName';
const LENGTHOFSTAY_TYPE = 'lengthOfStay';
const OCCUPANCY_TYPE = 'occupancy';
const BEDROOMS_TYPE = 'bedrooms';
const BATHROOMS_TYPE = 'bathrooms';
const PRICE_TYPE = 'price';

const NUMBER_TYPE = 'number';
const DROPDOWN_TYPE = 'dropdown';
const DATE_TYPE = 'date';
const TEXT_TYPE = 'text';

const FITLER_LIST = [LOCATION_TYPE, PRICE_TYPE, CHECKIN_TYPE, CHECKOUT_TYPE, LENGTHOFSTAY_TYPE, OCCUPANCY_TYPE, BEDROOMS_TYPE, BATHROOMS_TYPE];

interface IProps {
  filters: ICondoWeeksFilters;
  locations: any[];

  onFiltersChange: (filters: ICondoWeeksFilters) => void;
}

export function CondoWeeksFilters(props: IProps) {
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showCheckOkut, setShowCheckOut] = useState(false);
  const [showLocationName, setShowLocationName] = useState(false);
  const [showLengthOfStay, setShowLengthOfStay] = useState(false);
  const [showOccupancy, setShowOccupancy] = useState(false);
  const [showBedrooms, setShowBedrooms] = useState(false);
  const [showBathrooms, setShowBathrooms] = useState(false);
  const [showPrice, setShowPrice] = useState(false);
  const [condition, setCondition] = useState(EQUAL_FILTER);
  const [conditionDropdown, setConditionDropdown] = useState<string[]>([]);
  const [conditionValue, setConditionValue] = useState<string>('');
  const [conditionValue2, setConditionValue2] = useState<string>('');
  const [openSelect, setOpenSelect] = useState(false);
  
  const { filters, locations, onFiltersChange } = props;
  const { checkInCondition, fromCheckIn, toCheckIn, checkOutCondition, fromCheckOut, toCheckOut, locationName, lengthOfStay, priceCondition, startPrice, endPrice, occupancy, bedrooms, bathrooms } = filters;
  
  const locationNameDesc = locationName?.map((name) => `(${locations.find(l => l.locationId === name)?.city || ''}${locations.find(l => l.locationId === name)?.state ? `, ${locations.find(l => l.locationId === name)?.state}` : ''}${locations.find(l => l.locationId === name)?.country ? `, ${locations.find(l => l.locationId === name)?.country})` : ''}`).join(',');

  const selectRef = useRef<HTMLDivElement>(null);
  const openSelectRef = useRef<boolean>(false);

  const getConditionString = (condition: string) => {
    return condition === GREATER_FILTER ? 'Greater than: ' : condition === LOWER_FILTER ? 'Lower than: ' : condition === RANGE_FILTER ? 'Between: ' : '';
  }

  const getDateValueString = (valueFrom: string | null, valueTo: string | null) => {
    return (!isEmpty(valueFrom) && !isEmpty(valueTo) && valueFrom !== valueTo) ? `${moment(valueFrom, 'YYYY-MM-DD').format('MM/DD/YYYY')} - ${moment(valueTo, 'YYYY-MM-DD').format('MM/DD/YYYY')}` : (!isEmpty(valueTo)) ? moment(valueTo, 'YYYY-MM-DD').format('MM/DD/YYYY') : (!isEmpty(valueFrom)) ? moment(valueFrom, 'YYYY-MM-DD').format('MM/DD/YYYY') : '';
  }

  const getShowCheckIn = () => showCheckIn;
  const getShowCheckOkut = () => showCheckOkut;
  const getShowLocationName = () => showLocationName;
  const getShowLengthOfStay = () => showLengthOfStay;
  const getShowOccupancy = () => showOccupancy;
  const getShowBedrooms = () => showBedrooms;
  const getShowBathrooms = () => showBathrooms;
  const getShowPrice = () => showPrice;

  const getCheckIn = () => `${getConditionString(checkInCondition)}${getDateValueString(fromCheckIn, toCheckIn)}`;
  const getCheckOkut = () => `${getConditionString(checkOutCondition)}${getDateValueString(fromCheckOut, toCheckOut)}`;
  const getLocationName = () => locationNameDesc;
  const getLocationNameTooltip = () => locationNameDesc;
  const getLengthOfStay = () => lengthOfStay?.toString();
  const getOccupancy = () => occupancy?.toString();
  const getBedrooms = () => bedrooms?.toString();
  const getBathrooms = () => bathrooms?.toString();
  const getPrice = () => `${getConditionString(priceCondition)}${(startPrice && endPrice && startPrice !== endPrice) ? `${startPrice} - ${endPrice}` : endPrice ? endPrice : startPrice ? startPrice : ''}`;

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => { openSelectRef.current = openSelect; }, [openSelect]);

  const MAPPING_TYPE = {
    [CHECKIN_TYPE]: { isRange: true, title: 'check.in', inputType: DATE_TYPE, placeholder: 'Check-In' },
    [CHECKOUT_TYPE]: { isRange: true, title: 'check.out', inputType: DATE_TYPE, placeholder: 'Check-Out' },
    [LENGTHOFSTAY_TYPE]: { isRange: false, title: 'length.stay', inputType: NUMBER_TYPE, placeholder: 'Length of Stay' },
    [OCCUPANCY_TYPE]: { isRange: false, title: 'max.occupancy', inputType: NUMBER_TYPE, placeholder: 'Occupancy' },
    [BEDROOMS_TYPE]: { isRange: false, title: 'bedrooms', inputType: NUMBER_TYPE, placeholder: 'Bedrooms' },
    [BATHROOMS_TYPE]: { isRange: false, title: 'bathrooms', inputType: NUMBER_TYPE, placeholder: 'Bathrooms' },
    [LOCATION_TYPE]: { isRange: false, isDropdown: true, options: locations, isMultiple: true, title: 'location', inputType: TEXT_TYPE, placeholder: 'Location Name' },
    [PRICE_TYPE]: { isRange: true, title: 'price', inputType: NUMBER_TYPE, placeholder: 'Price' },
  }

  const MAPPING_VALUE_TYPE = {
    [CHECKIN_TYPE]: { setVisible: setShowCheckIn, getVisible: getShowCheckIn, getValueStr: getCheckIn, getValue: () => fromCheckIn, getValue2: () => toCheckIn, getCondition: () => checkInCondition },
    [CHECKOUT_TYPE]: { setVisible: setShowCheckOut, getVisible: getShowCheckOkut, getValueStr: getCheckOkut, getValue: () => fromCheckOut, getValue2: () => toCheckOut, getCondition: () => checkOutCondition },
    [LENGTHOFSTAY_TYPE]: { setVisible: setShowLengthOfStay, getVisible: getShowLengthOfStay, getValueStr: getLengthOfStay, getValue: () => lengthOfStay },
    [OCCUPANCY_TYPE]: { setVisible: setShowOccupancy, getVisible: getShowOccupancy, getValueStr: getOccupancy, getValue: () => occupancy },
    [BEDROOMS_TYPE]: { setVisible: setShowBedrooms, getVisible: getShowBedrooms, getValueStr: getBedrooms, getValue: () => bedrooms },
    [BATHROOMS_TYPE]: { setVisible: setShowBathrooms, getVisible: getShowBathrooms, getValueStr: getBathrooms, getValue: () => bathrooms },
    [LOCATION_TYPE]: { setVisible: setShowLocationName, getVisible: getShowLocationName, getValueStr: getLocationName, getValueStrTooltip: getLocationNameTooltip, getValue: () => locationName },
    [PRICE_TYPE]: { setVisible: setShowPrice, getVisible: getShowPrice, getValueStr: getPrice, getValue: () => startPrice, getValue2: () => endPrice, getCondition: () => priceCondition },
  }

  const handleClickOutside = (event: MouseEvent): void => {
    if (
      selectRef?.current && 
      !selectRef?.current?.contains(event.target as Node) &&
      !(event.target as HTMLElement).classList.contains('ant-select-selection-overflow') &&
      !(event.target as HTMLElement).closest('.ant-select-dropdown') &&
      openSelectRef.current
    ) {
      setOpenSelect(false);
    }
  };

  const resetFlags = (condition: string | null, value: string | null, value2: string | null, callback?: () => void) => {
    setShowCheckIn(false);
    setShowCheckOut(false);
    setShowLocationName(false);
    setShowLengthOfStay(false);
    setShowPrice(false);
    setShowOccupancy(false);
    setShowBedrooms(false);
    setShowBathrooms(false);
    setCondition(condition && !isEmpty(condition) ? condition : EQUAL_FILTER);
    setConditionDropdown([]);
    setConditionValue(value && !isEmpty(value) ? value : '');
    setConditionValue2(value2 && !isEmpty(value2) ? value2 : '');
    
    callback?.();
  }

  const handleReset = (type: string) => {
    const newFilters = { ...filters };

    if (type === CHECKIN_TYPE) {
      newFilters.checkInCondition = EQUAL_FILTER;
      newFilters.fromCheckIn = null;
      newFilters.toCheckIn = null;
    }
    if (type === CHECKOUT_TYPE) {
      newFilters.checkOutCondition = EQUAL_FILTER;
      newFilters.fromCheckOut = null;
      newFilters.toCheckOut = null;
    }
    if (type === LOCATION_TYPE) {
      newFilters.locationName = null;
    }
    if (type === LENGTHOFSTAY_TYPE) {
      newFilters.lengthOfStay = null;
    }
    if (type === OCCUPANCY_TYPE) {
      newFilters.occupancy = null;
    }
    if (type === BEDROOMS_TYPE) {
      newFilters.bedrooms = null;
    }
    if (type === BATHROOMS_TYPE) {
      newFilters.bathrooms = null;
    }
    if (type === PRICE_TYPE) {
      newFilters.priceCondition = EQUAL_FILTER;
      newFilters.startPrice = null;
      newFilters.endPrice = null;
    }

    onFiltersChange(newFilters);
    resetFlags(null, null, null);
  }

  const handleApply = (type: string) => {
    const newFilters = { ...filters };

    if (type === CHECKIN_TYPE) {
      newFilters.checkInCondition = condition;
      newFilters.fromCheckIn = !isEmpty(conditionValue) ? conditionValue : null;
      newFilters.toCheckIn = !isEmpty(conditionValue2) ? conditionValue2 : null;
    }
    if (type === CHECKOUT_TYPE) {
      newFilters.checkOutCondition = condition;
      newFilters.fromCheckOut = !isEmpty(conditionValue) ? conditionValue : null;
      newFilters.toCheckOut = !isEmpty(conditionValue2) ? conditionValue2 : null;
    }
    if (type === LOCATION_TYPE) {
      newFilters.locationName = conditionDropdown;
    }
    if (type === LENGTHOFSTAY_TYPE) {
      newFilters.lengthOfStay = !isEmpty(conditionValue) ? Number(conditionValue) : null;
    }
    if (type === OCCUPANCY_TYPE) {
      newFilters.occupancy = !isEmpty(conditionValue) ? Number(conditionValue) : null;
    }
    if (type === BEDROOMS_TYPE) {
      newFilters.bedrooms = !isEmpty(conditionValue) ? Number(conditionValue) : null;
    }
    if (type === BATHROOMS_TYPE) {
      newFilters.bathrooms = !isEmpty(conditionValue) ? Number(conditionValue) : null;
    }
    if (type === PRICE_TYPE) {
      newFilters.priceCondition = condition;
      newFilters.startPrice = !isEmpty(conditionValue) ? Number(conditionValue) : null;
      newFilters.endPrice = !isEmpty(conditionValue2) ? Number(conditionValue2) : null;
    }

    onFiltersChange(newFilters);
    resetFlags(null, null, null);
  }

  const renderFilterInput = (display: boolean, inputType: string, value: string, placeholder: string, options: string[] | null, setValue: (value: string) => void) => {
    if (!display) {
      return null;
    }

    if (inputType === DROPDOWN_TYPE) {
      return (
        <Select value={value} onChange={e => setValue(e)} allowClear>
          {options?.map((o, i) => <Select.Option key={`index_${i}`} value={o}>{o}</Select.Option>)}
        </Select>);
    }

    return (
      <Input
        value={value ? value : ''}
        placeholder={`${placeholder}`}
        type={inputType}
        allowClear
        onKeyDown={(e) => {
          if (inputType === NUMBER_TYPE && !['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(e.key)) {
            e.preventDefault();
          }
        }}
        min={0}
        step={1}
        onChange={(e) => {
          setValue(e.target.value?.toString());
        }}
      ></Input>
    );
  }

  const handleDropdownVisibleChange = (visible: boolean) => {
    // Only allow opening if it's not already open
    if (visible && !openSelect) {
      setOpenSelect(true);
    }
  };

  const renderCondoFilterItem = (type: string) => {
    const { isRange, inputType, placeholder, title, isMultiple, options, isDropdown } = get(MAPPING_TYPE, `${type}`);
    const { setVisible, getVisible, getValueStr, getValueStrTooltip, getValue, getValue2, getCondition } = get(MAPPING_VALUE_TYPE, `${type}`);
    
    const valueStr = getValueStr();
    const valueStrTooltip = getValueStrTooltip ? getValueStrTooltip() : null;
    const value = getValue();
    const value2 = getValue2 ? getValue2() : null;
    const conditionSelected = getCondition ? getCondition() : null;
    const visible = getVisible();

    const menu = (
      <Menu>
        <Menu.Item key="content">
          <div className="condo-weeks-filters__item-content" ref={selectRef}>
            {isDropdown ? (
              <Select 
                value={conditionDropdown}
                open={openSelect}
                onDropdownVisibleChange={handleDropdownVisibleChange}
                onChange={(e) => {setConditionDropdown(e); setOpenSelect(false);}} 
                mode={isMultiple? 'multiple' : undefined}
                onClear={() => setConditionDropdown([])}
                filterOption={(input, option) => (option?.children as any ?? '')?.toLowerCase()?.includes(input.toLowerCase())}
                allowClear
                showSearch
                showArrow
              >
                {options?.map((o: any, i: number) => 
                  <Select.Option key={`index_${i}`} value={o?.locationId}>{`${o?.city || ''}${o?.state ? `, ${o?.state}` : ''}${o?.country ? `, ${o?.country}` : ''}`}</Select.Option>)}
              </Select>) : null}

            {isRange && !isDropdown ? (
              <Select value={condition} onChange={(e) => setCondition(e)}>
                <Select.Option value={EQUAL_FILTER}>Equal</Select.Option>
                <Select.Option value={LOWER_FILTER}>Lower than</Select.Option>
                <Select.Option value={GREATER_FILTER}>Greater than</Select.Option>
                <Select.Option value={RANGE_FILTER}>Between</Select.Option>
              </Select>) : null}
            
            {!isDropdown ? renderFilterInput(true, inputType, conditionValue, condition === RANGE_FILTER ? `${placeholder} from` : placeholder, null, setConditionValue) : null}

            {!isDropdown ? renderFilterInput(condition === RANGE_FILTER, inputType, conditionValue2, `${placeholder} to`, null, setConditionValue2) : null}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '10px' }}>
              <WhiteButton onClick={() => handleReset(type)} disabled={!valueStr || isEmpty(valueStr)}><FormattedMessage id="reset" /></WhiteButton>
              <BlueButton onClick={() => handleApply(type)}><FormattedMessage id="apply" /></BlueButton>
            </div>
          </div>
        </Menu.Item>
      </Menu>
    );
    
    return (
      <Dropdown 
        overlay={menu} 
        trigger={['click']} 
        open={visible} 
        overlayClassName="condo-weeks-filters__dropdown"
        onOpenChange={open => {
          if (open) {
            if (type === CHECKIN_TYPE) {
              setCondition(checkInCondition);
              setConditionValue(fromCheckIn || '');
              setConditionValue2(toCheckIn || '');
            } else if (type === CHECKOUT_TYPE) {
              setCondition(checkOutCondition);
              setConditionValue(fromCheckOut || '');
              setConditionValue2(toCheckOut || '');
            } else if (type === LOCATION_TYPE) {
              setConditionDropdown(locationName || []);
            } else if (type === LENGTHOFSTAY_TYPE) {
              setConditionValue(lengthOfStay?.toString() || '');
            } else if (type === OCCUPANCY_TYPE) {
              setConditionValue(occupancy?.toString() || '');
            } else if (type === BEDROOMS_TYPE) {
              setConditionValue(bedrooms?.toString() || '');
            } else if (type === BATHROOMS_TYPE) {
              setConditionValue(bathrooms?.toString() || '');
            } else if (type === PRICE_TYPE) {
              setCondition(priceCondition);
              setConditionValue(startPrice?.toString() || '');
              setConditionValue2(endPrice?.toString() || '');
            }
          }
        }}
      >
        <div className="condo-weeks-filters__item" onClick={() => {
          resetFlags(conditionSelected, value?.toString(), value2?.toString());
          setVisible(!visible);
        }}>
          <div className="item-title">
            <FormattedMessage id={title} />
            <div className="item-arrow">
              <FontAwesomeIcon icon={faChevronDown} />
            </div>
          </div>
          <div style={{ color: '#000000', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'relative', paddingRight: '25px' }}>
            <Tooltip title={valueStrTooltip || valueStr}>{valueStr}</Tooltip>
            {!isEmpty(valueStr) && (
              <div className="condo-weeks-filters__item-delete" onClick={() => handleReset(type)}>
                <FontAwesomeIcon icon={faTimesCircle} />
              </div>)}
          </div>
        </div>
      </Dropdown>);
  }

  return (
    <div className="condo-weeks-filters">
      <div className="container-fluid">
        <div className="row">
          {FITLER_LIST.map((f, i) => (
            <div key={`index_table_${i}`} className={`col-lg-${f === LOCATION_TYPE ? '8' : '4'}`}>
              {renderCondoFilterItem(f)}
            </div>))}
        </div>
      </div>
    </div>
  );
}
